/**
 * Handy-Teile des Monats (Board „Verfügbarkeit · Handy“): unter dem Raster der Weg zu den
 * Kanalregeln und was sonst nur der Grundkalender zeigt, über der Leiste die Auswahl mit
 * „Bearbeiten“ (oder „Tagesdetail“ ohne Edit) und das Blatt mit demselben Inhalt wie am Desktop.
 * Das Blatt verliert beim Schließen nichts – die Werte gehören dem Monat.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Lock } from 'lucide-react';
import type { AvailabilityDistributionChannelCloseout } from '@/shared/api';
import { BottomSheet } from '@/shared/components';
import type { SelectedRange } from '../calendar/rangeSelectionModel';
import { totalNights } from '../calendar/rangeSelectionModel';
import { daysLabel, selectionTitle } from './monthEditorModel';

interface Props {
  grid: ReactNode;
  side: ReactNode;
  inspectOnly: boolean;
  /** Gerade vorgemerkt – die Meldung steht unter dem Raster, nicht im geschlossenen Blatt. */
  pushed: boolean;
  apartmentName: string;
  ranges: readonly SelectedRange[];
  closeouts: readonly AvailabilityDistributionChannelCloseout[];
  onManageRules?: () => void;
  extras?: ReactNode;
}

export function MonthMobileActions({ grid, side, ranges, closeouts, ...props }: Props) {
  const [open, setOpen] = useState(false);
  // Ohne Auswahl schließt das Blatt; sonst spränge es beim nächsten Antippen von selbst auf.
  useEffect(() => {
    if (ranges.length === 0) setOpen(false);
  }, [ranges.length]);
  const failed = closeouts.filter((rule) => rule.syncStatus === 'Failed').length;
  return (
    <div className="flex flex-col gap-3">
      {grid}
      {props.pushed && (
        <p
          role="status"
          className="bg-forest-soft text-forest-deep rounded-tile px-4 py-3 text-[13px] font-semibold"
        >
          Änderungen vorgemerkt – sie werden an Channex übertragen. Den Stand siehst du oben im
          Kopf.
        </p>
      )}
      {props.onManageRules && (
        <button
          type="button"
          onClick={props.onManageRules}
          className="bg-card border-hairline rounded-tile flex min-h-14 items-center gap-3 border px-4 text-left"
        >
          <span className="flex grow flex-col">
            <span className="text-sm font-bold">Kanalregeln</span>
            <span className={failed > 0 ? 'text-sealwax text-xs' : 'text-ink-soft text-xs'}>
              {closeouts.length === 0
                ? 'Ansehen und bearbeiten'
                : `${closeouts.length} in diesem Monat${failed > 0 ? ` · ${failed} fehlgeschlagen` : ''}`}
            </span>
          </span>
          <span aria-hidden>→</span>
        </button>
      )}
      {props.extras}
      {side && ranges.length > 0 && (
        <div className="bg-ink rounded-tile shadow-float sticky bottom-3 z-30 flex items-center gap-3 px-4 py-3 text-white">
          <span className="flex min-w-0 grow flex-col">
            <span className="truncate text-sm font-bold">{selectionTitle(ranges)}</span>
            <span className="text-on-forest-soft text-xs">
              {props.apartmentName} · {daysLabel(totalNights(ranges))}
            </span>
          </span>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-pill text-ink h-11 shrink-0 bg-white px-4 text-sm font-bold"
          >
            {props.inspectOnly ? 'Tagesdetail' : 'Bearbeiten'}
          </button>
        </div>
      )}
      {side && (
        <BottomSheet
          open={open && ranges.length > 0}
          onOpenChange={setOpen}
          title={props.inspectOnly ? 'Tagesdetail' : 'Zeitraum bearbeiten'}
        >
          <div className="-mx-1">{side}</div>
        </BottomSheet>
      )}
    </div>
  );
}

/** Hinweis unter dem Raster: wie man wählt und was die Zeichen heißen. */
export function MonthHint({
  canEdit,
  inspectOnly,
  mobile,
}: {
  canEdit: boolean;
  inspectOnly: boolean;
  mobile: boolean;
}) {
  return (
    <p className="text-ink-soft flex flex-wrap gap-x-4 gap-y-1 px-[18px] py-3 text-xs">
      {!canEdit ? (
        <span>
          {inspectOnly
            ? `Nur ansehen · Tag ${mobile ? 'antippen' : 'anklicken'} für das Tagesdetail`
            : 'Nur ansehen'}
        </span>
      ) : mobile ? (
        <span>Ersten und letzten Tag antippen</span>
      ) : (
        <>
          <span>Ersten und letzten Tag klicken, um einen Zeitraum zu wählen.</span>
          <span>Strg-Klick: weiterer Zeitraum · bis zu 50</span>
        </>
      )}
      <span className="inline-flex items-center gap-1">
        <Lock size={11} strokeWidth={2.6} aria-hidden /> von Hand gesetzt
      </span>
      {!mobile && <span>gedämpft: Standardpreis · 2 N. = Mindestnächte</span>}
    </p>
  );
}
